/**
 * @module briefing-pdf-export
 * @purpose Build and download a multi-page PDF of the briefing board's top-N clusters using jsPDF
 *   text APIs directly — selectable text, reliable pagination, NO DOM/canvas capture (html2canvas
 *   could not reliably capture the tall off-screen document).
 * @depends jspdf, lib/deterministic-headline, types/reasoning, i18next
 * @dependedOnBy BriefingBoardView
 */

import { jsPDF } from 'jspdf'
import type { TFunction } from 'i18next'
import { composeDeterministicHeadline } from '../../../../../lib/deterministic-headline'
import type { ClusterBriefing, ClusterSummary } from '../../../../../types/reasoning'

type AnalysisT = TFunction<'analysis'>

interface PdfSection {
  heading: string
  items: string[]
}

interface PdfCluster {
  rank: string
  tag: string
  isAi: boolean
  headline: string
  summary: string
  sections: PdfSection[]
  facts: string
}

export interface BriefingPdfContent {
  title: string
  caseRun: string
  generated: string
  footer: string
  clusters: PdfCluster[]
}

export interface BriefingPdfArgs {
  clusters: ClusterSummary[]
  briefings: Map<string, ClusterBriefing>
  t: AnalysisT
  caseId: string
  runId: string
  total: number
  generatedAt: string
}

// =============================================================================
// CONTENT BUILDER (pure — unit-testable without jsPDF)
// =============================================================================

function fallbackHeadline(cluster: ClusterSummary, t: AnalysisT): string {
  const det = composeDeterministicHeadline(cluster)
  const flag = t(`reasoning.briefingBoard.tile.dominantFlag.${det.dominantFlag}` as const)
  return `${det.directorySegment} — ${t('reasoning.briefingBoard.tile.files', { count: det.fileCount })}; ${flag}`
}

function factsLine(cluster: ClusterSummary, t: AnalysisT): string {
  const parts = [t('reasoning.briefingBoard.tile.files', { count: cluster.fileCount })]
  if (cluster.referencedByCount > 0) {
    parts.push(t('reasoning.briefingBoard.tile.referencedBy', { count: cluster.referencedByCount }))
  }
  if (cluster.hasPresenceEvidence) parts.push(t('reasoning.briefingBoard.tile.evidence.presence'))
  if (cluster.hasReferenceEvidence) parts.push(t('reasoning.briefingBoard.tile.evidence.reference'))
  if (cluster.hasExecutionEvidence) parts.push(t('reasoning.briefingBoard.tile.evidence.execution'))
  return parts.join('  ·  ')
}

function aiSections(briefing: ClusterBriefing, t: AnalysisT): PdfSection[] {
  return [
    { heading: t('reasoning.briefingBoard.pdf.sections.supportedPoints'), items: briefing.supported_points },
    { heading: t('reasoning.briefingBoard.pdf.sections.temporal'), items: briefing.temporal_observations },
    { heading: t('reasoning.briefingBoard.pdf.sections.hashLolbas'), items: briefing.hash_and_lolbas_observations },
    { heading: t('reasoning.briefingBoard.pdf.sections.execution'), items: briefing.execution_context_observations },
    { heading: t('reasoning.briefingBoard.pdf.sections.notEstablished'), items: briefing.not_established },
  ].filter((section) => section.items.length > 0)
}

function toPdfCluster(cluster: ClusterSummary, briefing: ClusterBriefing | undefined, t: AnalysisT): PdfCluster {
  const isAi = briefing !== undefined
  return {
    rank: t('reasoning.clusterCard.rank', { position: cluster.rankPosition }),
    tag: isAi
      ? t('reasoning.briefingBoard.tile.aiSummary')
      : t('reasoning.briefingBoard.tile.machineGenerated'),
    isAi,
    headline: briefing?.headline ?? fallbackHeadline(cluster, t),
    summary: briefing?.summary ?? '',
    sections: briefing !== undefined ? aiSections(briefing, t) : [],
    facts: factsLine(cluster, t),
  }
}

export function buildBriefingContent(args: BriefingPdfArgs): BriefingPdfContent {
  const { clusters, briefings, t, caseId, runId, total, generatedAt } = args
  return {
    title: t('reasoning.briefingBoard.pdf.title'),
    caseRun: t('reasoning.briefingBoard.pdf.caseRun', { caseId, runId }),
    generated: t('reasoning.briefingBoard.pdf.generated', { date: generatedAt, shown: clusters.length, total }),
    footer: t('reasoning.briefingBoard.pdf.footer', { date: generatedAt }),
    clusters: clusters.map((cluster) => toPdfCluster(cluster, briefings.get(cluster.clusterId), t)),
  }
}

// =============================================================================
// jsPDF RENDERING
// =============================================================================

const MARGIN = 14
const PT_TO_MM = 0.3528
const LINE_SPACING = 1.15
const BASELINE_RATIO = 0.8
// Hex colours (jsPDF accepts hex strings for setTextColor / setDrawColor).
const ACCENT = '#4e5e82'
const INK = '#1f2937'
const BODY = '#374151'
const MUTED = '#6b7280'
const LIME = '#4d7c0f'
const RULE_COLOR = '#e5e7eb'

interface Cursor {
  doc: jsPDF
  y: number
  pageH: number
  contentW: number
}

interface TextOpts {
  size: number
  style?: 'normal' | 'bold' | 'italic'
  color?: string
  indent?: number
  gap?: number
}

/** Write wrapped text at the cursor, breaking to a new page line-by-line as needed. */
function writeText(cur: Cursor, str: string, opts: TextOpts): void {
  const { size, style = 'normal', color = INK, indent = 0, gap = 1.2 } = opts
  cur.doc.setFont('helvetica', style)
  cur.doc.setFontSize(size)
  cur.doc.setTextColor(color)
  const lines = cur.doc.splitTextToSize(str, cur.contentW - indent) as string[]
  const lineHeight = size * PT_TO_MM * LINE_SPACING
  for (const line of lines) {
    if (cur.y + lineHeight > cur.pageH - MARGIN) {
      cur.doc.addPage()
      cur.y = MARGIN
    }
    cur.doc.text(line, MARGIN + indent, cur.y + lineHeight * BASELINE_RATIO)
    cur.y += lineHeight
  }
  cur.y += gap
}

function writeCluster(cur: Cursor, cluster: PdfCluster): void {
  writeText(cur, `${cluster.rank}  ·  ${cluster.tag}`, {
    size: 8,
    style: 'bold',
    color: cluster.isAi ? LIME : MUTED,
    gap: 0.6,
  })
  writeText(cur, cluster.headline, { size: 11, style: 'bold', color: INK, gap: 1 })
  if (cluster.summary.length > 0) writeText(cur, cluster.summary, { size: 9.5, color: INK })
  for (const section of cluster.sections) {
    writeText(cur, section.heading, { size: 8, style: 'bold', color: ACCENT, gap: 0.4 })
    for (const item of section.items) {
      writeText(cur, `•  ${item}`, { size: 9, color: BODY, indent: 3, gap: 0.3 })
    }
  }
  writeText(cur, cluster.facts, { size: 8, color: MUTED, gap: 1 })
  cur.doc.setDrawColor(RULE_COLOR)
  cur.doc.line(MARGIN, cur.y, MARGIN + cur.contentW, cur.y)
  cur.y += 3
}

/** Build the structured content and render + download it as a multi-page A4 PDF. */
export function exportBriefingBoardPdf(args: BriefingPdfArgs, filename: string): void {
  const content = buildBriefingContent(args)
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageW = doc.internal.pageSize.getWidth()
  const cur: Cursor = { doc, y: MARGIN, pageH: doc.internal.pageSize.getHeight(), contentW: pageW - MARGIN * 2 }

  writeText(cur, content.title, { size: 16, style: 'bold', color: ACCENT, gap: 0.6 })
  writeText(cur, content.caseRun, { size: 8, color: MUTED, gap: 0.4 })
  writeText(cur, content.generated, { size: 8, color: MUTED, gap: 1 })
  doc.setDrawColor(ACCENT)
  doc.line(MARGIN, cur.y, MARGIN + cur.contentW, cur.y)
  cur.y += 4

  for (const cluster of content.clusters) writeCluster(cur, cluster)

  writeText(cur, content.footer, { size: 7, color: MUTED, gap: 0 })
  doc.save(filename)
}
